import { DEFAULT_LOCALE, LOCALE_BY_COUNTRY, isLocale, type Locale } from './types'

/**
 * Which language to open in.
 *
 * ## The order, and why it is this order
 *
 * 1. **What this person chose**, if they ever chose. It is the only signal that is a
 *    statement rather than an inference, so nothing may override it — not a different
 *    browser setting, not a trip abroad.
 * 2. **The browser's languages.** `navigator.languages` is a list the reader configured, in
 *    their own order of preference. It is the best guess available before any network call
 *    and it costs nothing.
 * 3. **The country the request came from.** Only when the browser said nothing we speak —
 *    a Mongolian reader on an English-only laptop is exactly the case this catches.
 * 4. **English**, because that is the catalogue that is complete.
 *
 * ## The country step is first-party
 *
 * Every one of these sites is behind Cloudflare, which answers `/cdn-cgi/trace` on the
 * site's **own** origin with a `loc=` line naming the country it resolved. So there is no
 * geo-IP service to call, no third party learning who is reading, no API key, and no
 * dependency — and on any host that is not behind Cloudflare (a laptop, a preview served by
 * `vite`) it simply 404s and the step is skipped.
 *
 * It runs **after** first paint and never blocks it. A page that waits on the network to
 * decide what language to render in is a page that renders nothing for 300ms.
 */

/** Step 1. A choice, if one was ever made. `null` when storage is unavailable or empty. */
export function storedLocale(storage: Pick<Storage, 'getItem'> | undefined, key: string): Locale | null {
  try {
    const value = storage?.getItem(key)
    return isLocale(value) ? value : null
  } catch {
    // Reading `localStorage` THROWS in a private window rather than returning null.
    return null
  }
}

/**
 * Step 2. The browser's preference list.
 *
 * Matched on the language subtag, so `mn-MN`, `mn-Cyrl-MN` and `mn` all count — a reader
 * whose browser says `mn-Cyrl-MN` wants Mongolian, and an exact-match check would miss them.
 */
export function browserLocale(languages: readonly string[] | undefined): Locale | null {
  for (const tag of languages ?? []) {
    const primary = tag.toLowerCase().split('-')[0]
    if (isLocale(primary)) return primary
  }
  return null
}

/** Step 3, given a country. Separated from the fetch so the mapping is testable on its own. */
export function countryLocale(country: string | null | undefined): Locale | null {
  if (!country) return null
  return LOCALE_BY_COUNTRY[country.toUpperCase()] ?? null
}

/**
 * The `loc=` line out of Cloudflare's trace, or `null` for anything unexpected.
 *
 * Deliberately tolerant: this is a best-effort hint, and a parse failure must mean "no
 * opinion" rather than an error a visitor sees.
 */
export function parseCloudflareTrace(body: string): string | null {
  for (const line of body.split('\n')) {
    const [key, value] = line.split('=')
    if (key === 'loc' && value) return value.trim()
  }
  return null
}

/**
 * Step 3, over the network. Never throws, and never waits long.
 *
 * The abort is not optional: this runs on every first visit, and a hanging request would
 * leave the "which language?" question open for as long as the connection took to give up.
 */
export async function fetchCountry(timeoutMs = 1500): Promise<string | null> {
  if (typeof fetch !== 'function') return null
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch('/cdn-cgi/trace', {
      signal: controller.signal,
      // A country is not worth a cookie.
      credentials: 'omit',
      cache: 'no-store',
    })
    if (!response.ok) return null
    return parseCloudflareTrace(await response.text())
  } catch {
    // Aborted, offline, not behind Cloudflare, or blocked. All mean the same thing here.
    return null
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Steps 1, 2 and 4 — everything that can be decided without the network, which is what the
 * first render uses.
 */
export function resolveLocaleSync(input: {
  storage?: Pick<Storage, 'getItem'>
  storageKey: string
  languages?: readonly string[]
}): { locale: Locale; chosen: boolean; needsCountry: boolean } {
  const stored = storedLocale(input.storage, input.storageKey)
  if (stored) return { locale: stored, chosen: true, needsCountry: false }

  const browser = browserLocale(input.languages)
  if (browser) return { locale: browser, chosen: false, needsCountry: false }

  // Nothing the browser said is a language we speak, so the country is worth asking about.
  return { locale: DEFAULT_LOCALE, chosen: false, needsCountry: true }
}
